import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import { useCustomerValue } from "providers/CustomerContext";
import { getVendorList } from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { getVendorStatusCount } from "services/data.internal.service";
import { TableCard } from "ui-components/Layout";
import { deleteVendor } from "services/data.internal.service";
import { activeVendor } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { InfoButton } from "ui-components/Button";
import { useTeamValue } from "providers/TeamContext";

function VendorList() {
  const {permissions, allowAll} = useTeamValue();
  const { customerCode } = useCustomerValue();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["vendor-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };

  const addPermission = allowAll || (Boolean(permissions["Vendor"]) && permissions["Vendor"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Vendor"]) && permissions["Vendor"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Vendor"]) && permissions["Vendor"].findIndex(b => b==="deactivate") !== -1);

  useQuery({
    queryKey: ["vendor-list", { customerCode, active }],
    queryFn: () => getVendorList({ customerCode, active }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["vendor-list", "status", { customerCode }],
    queryFn: () => getVendorStatusCount({ customerCode }),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deleteVendor, { onSuccess, onError });

  const activeMutation = useMutation(activeVendor, { onSuccess, onError });

  const onEdit = (req) => navigate("/setup/vendor/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.vendorCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.vendorCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Vendor Code", accessorKey: "vendorCode" },
    { header: "Default", accessorKey: "isDefault" },
    { header: "Customer Code", accessorKey: "customerCode" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { vendorCode, customerCode } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit vendor"
                disabled={!editPermission}
                onClick={() => onEdit({ vendorCode, customerCode })}
              />
              <DelButton
                title="deactivate vendor"
                disabled={!deactivatePermission}
                onClick={() => onDelete({ vendorCode, customerCode })}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate vendor"
              disabled={!editPermission}
              onClick={() => onActive({ vendorCode, customerCode })}
            />
          );
        }
      },
    },
  ];

  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" alignItems="flex-start" m={1.2}>
          <InfoButton disabled={!addPermission} onClick={() => navigate("/setup/vendor/add")}>
            <Icon>add</Icon>&nbsp;vendor
          </InfoButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default VendorList;
